import axios from "axios";
import { GlobalService } from "./global.service";
import { webSocketService } from "../../../services/websocketService";

interface HttpServiceFunctions {
    handleResponseError: (err: any) => any;
    get: (endpoint: string, auth?: boolean, headers?: any) => Promise<any>;
    post: (endpoint: string, data: any, headers?: any, auth?: boolean, isRefresh?: boolean) => Promise<any>;
    put: (endpoint: string, data: any, headers?: any, auth?: boolean) => Promise<any>;
    patch: (endpoint: string, data: any, headers?: any, auth?: boolean) => Promise<any>;
    deleteRecord: (endpoint: string, headers?: any, auth?: boolean) => Promise<any>;
    deleteWithData: (endpoint: string, data: any, headers?: any, auth?: boolean) => Promise<any>;
    getById: (endpoint: string, id: string, headers?: any, auth?: boolean) => Promise<any>;
    getBlob: (endpoint: string, auth?: boolean, headers?: any) => Promise<Blob>;
    refreshToken: () => Promise<any>;
}

const axiosInstance = axios.create({
    baseURL: GlobalService.getBaseUrl(),
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

axiosInstance.interceptors.request.use(
    async (config: any) => {
        const token = await GlobalService.getStorage("token");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers["Authorization"] = "Bearer " + token;
                        return axiosInstance(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }
            isRefreshing = true;
            try {
                const newToken = await refreshToken();
                originalRequest.headers["Authorization"] = "Bearer " + newToken;
                processQueue(null, newToken);
                return axiosInstance(originalRequest);
            } catch (err) {
                processQueue(err, null);
                await GlobalService.clearStorage(); // optionally log out
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(handleResponseError(error));
    }
);

const handleResponseError = (err: any): any => {
    if (!err.response) {
        console.error(err);
        return {
            message: "Network connectivity issues",
            statusCode: 0,
        };
    }

    return {
        data: err.response.data,
        message: err.response.data?.message || "Unknown error occurred",
        error: err.response.data?.error || "Error",
        statusCode: err.response.status,
    };
};

const get = async (endpoint: string, auth = false, headers: any = {}): Promise<any> => {
    try {
        const response = await axiosInstance.get(GlobalService.getBaseUrl() + endpoint, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const post = async (endpoint: string, data: any, headers: any = {}, auth = false, isRefresh = false): Promise<any> => {
    try {
        const response = await axiosInstance.post(GlobalService.getBaseUrl() + endpoint, data, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const put = async (endpoint: string, data: any, headers: any = {}, auth = false): Promise<any> => {
    try {
        const response = await axiosInstance.put(GlobalService.getBaseUrl() + endpoint, data, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const patch = async (endpoint: string, data: any, headers: any = {}, auth = false): Promise<any> => {
    try {
        const response = await axiosInstance.patch(GlobalService.getBaseUrl() + endpoint, data, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const deleteRecord = async (endpoint: string, headers: any = {}, auth = false): Promise<any> => {
    try {
        const response = await axiosInstance.delete(GlobalService.getBaseUrl() + endpoint, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const deleteWithData = async (endpoint: string, data: any, headers: any = {}, auth = false): Promise<any> => {
    try {
        const response = await axiosInstance({
            method: "post",
            url: GlobalService.getBaseUrl() + endpoint,
            data,
            headers,
        });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const getById = async (endpoint: string, id: string, headers: any = {}, auth = false): Promise<any> => {
    try {
        const response = await axiosInstance.get(`${endpoint}/${id}`, { headers });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const refreshToken = async (): Promise<any> => {
    try {
        const refreshToken = await GlobalService.getStorage("refreshToken");
        if (refreshToken) {
            const response = await axios.post(GlobalService.getBaseUrl() + "api/v1/auth/refresh", {}, { headers: { Authorization: `Bearer ${refreshToken}` } });
            const newToken = response.data.token;
            await GlobalService.setStorage("token", newToken);
            await GlobalService.setStorage("refreshToken", response.data.refreshToken);
            
            // Reconnect WebSocket with new token
            try {
                const userData = await GlobalService.getStorage("user");
                if (userData && userData.id) {
                    console.log("🔄 Token refreshed, triggering WebSocket reconnection...");
                    await webSocketService.reconnectWithNewToken(newToken, userData.id);
                }
            } catch (wsError) {
                console.warn("⚠️ WebSocket reconnection failed after token refresh:", wsError);
                // Don't fail the token refresh if WebSocket reconnection fails
            }
            
            return Promise.resolve(newToken);
        }
        else {
            return Promise.reject(false);
        }
    } catch (err) {
        return Promise.reject(false);
    }
};

const getBlob = async (endpoint: string, auth = false, headers: any = {}): Promise<Blob> => {
    try {
        const token = await GlobalService.getStorage("token");
        const requestHeaders = {
            ...headers,
            ...(auth && token ? { Authorization: `Bearer ${token}` } : {}),
        };
        
        const response = await axiosInstance.get(GlobalService.getBaseUrl() + endpoint, { 
            headers: requestHeaders,
            responseType: "blob"
        });
        return response.data;
    } catch (err) {
        return Promise.reject(err);
    }
};

const HttpService: HttpServiceFunctions = {
    handleResponseError,
    get,
    post,
    put,
    patch,
    deleteRecord,
    deleteWithData,
    getById,
    getBlob,
    refreshToken,
};

export { HttpService };
